import dayjs from "dayjs";
import { hasValue, isNotEmptyElement } from "./helpers";

//* Global 

export const hasFilterData = (filters) => Object.values(filters).some(isNotEmptyElement);

export const parseFlexibleDate = (str) => { 
  if (!str) return null; 
  const parts = str.trim().split('/'); 

  if (parts.length !== 3) return null; 
    
  const isYearFirst = parts[0].length === 4; 
    
  const [year, month, day] = isYearFirst 
  ? [parts[0], parts[1], parts[2]] 
  : [parts[2], parts[1], parts[0]]; 

  const parsed = dayjs(`${year}-${month}-${day}`, 'YYYY-MM-DD'); 
  
  return parsed.isValid() ? parsed : null; 
};

export const getDateRangeByShortcut = (type) => {
  const today = dayjs(); 
  
  switch (type) { 
    case 'today': 
      return { 
        start: today, 
        end: today,
      }; 
      
    case '7days': 
      return { 
        start: today.subtract(7, 'day'), 
        end: today, 
      };
      
    case '30days': 
      return { 
        start: today.subtract(30, 'day'), 
        end: today, 
      }; 
    
    case '90days': 
      return { 
        start: today.subtract(90, 'day'), 
        end: today, 
      }; 
      
    case '6months': 
      return { 
        start: today.subtract(6, 'month'), 
        end: today, 
      }; 
    
    case 'thisMonth': 
      return { 
        start: today.startOf('month'), 
        end: today.endOf('month'), 
      }; 
    
    default: 
      return null; 
  } 
};

export const isSameDateRange = (startDate, endDate, shortcut) => { 
  const range = getDateRangeByShortcut(shortcut); 
  
  if (!range || !startDate || !endDate) return false; 
  return ( 
    startDate.isSame(range.start, 'day') && 
    endDate.isSame(range.end, 'day')
  ); 
};

//* Form Fills Filters 

export const FORM_FILLS_FILTERS_MAP = [
  ["projectIds", "project_ids"],
  ["workIds", "work_ids"],
  ["resourcePlaceIds", "resource_place_ids"],
  ["workAssigneeIds", "user_ids"],
  ["orderBy", "field_id"],
  ["ordering", "order_type"],
  ["q", "search"],
  ["dueDateRangeTime", "range_time"],
];

export const buildExportFilters = (filters) =>
  FORM_FILLS_FILTERS_MAP.reduce(
    (acc, [srcKey, destKey]) => {
      const value = filters[srcKey];

      if (hasValue(value)) {
        acc[destKey] = value;
      }

      return acc;
    },
    { form_id: filters.formIds ?? [] },
  );

export const WINDOW_FORM_FILLS_FILTERS_MAP = [
  ["formIds", "_filteredFormId", []],
  ["projectIds", "_filteredProjectIds", []],
  ["workIds", "_filteredWorkIds", []],
  ["workAssigneeIds", "_filteredUserIds", []],
  ["resourcePlaceIds", "_filteredResourcePlaceIds", []],
  ["orderBy", "_filteredFieldId", []],
  ["ordering", "_orderType", ""],
  ["q", "_searchQuery", ""],
  ["dueDateRangeTime", "_filteredRangeTime", ""],
];

export const applyWindowformFillsFilters = (filters) => {
  WINDOW_FORM_FILLS_FILTERS_MAP.forEach(([filterKey, windowKey, defaultValue]) => {
    window[windowKey] = filters[filterKey] ?? defaultValue;
  });

  if (
    Array.isArray(window._filteredFormId) &&
    window._filteredFormId.length === 1
  ) {
    window._filteredFormId = window._filteredFormId[0];
  }
};

export const FORM_FILLS_FILTERS_URL_MAP   = [
  { key: "formIds", param: "form_fill[form_ids][]", isArray: true },
  { key: "resourcePlaceIds", param: "form_fill[resource_place_ids][]", isArray: true },
  { key: "workIds", param: "form_fill[work_ids][]", isArray: true },
  { key: "projectIds", param: "form_fill[project_ids][]", isArray: true },
  { key: "workAssigneeIds", param: "form_fill[user_ids][]", isArray: true },
  { key: "dueDateRangeTime", param: "range_time" },
  { key: "orderBy", param: "field_id" },
  { key: "ordering", param: "order_type" },
  { key: "q", param: "q" },
];

export const buildQueryParams = (filters, viewType) => {
  const params = new URLSearchParams();

  FORM_FILLS_FILTERS_URL_MAP.forEach(({ key, param, isArray }) => {
    const value = filters[key];

    if (!hasValue(value)) return;

    if (isArray) {
      value.forEach((item) => params.append(param, item));
    } else {
      params.append(param, value);
    }
  });

  if (viewType) {
    params.append("view_type", viewType);
  }

  return params.toString();
};

export const getInitialFilters = () => {
  const params = new URLSearchParams(window.location.search);

  return FORM_FILLS_FILTERS_URL_MAP.reduce((acc, { key, param, isArray }) => {
    acc[key] = isArray
      ? params.getAll(param).map(Number)
      : params.get(param) || null;

    return acc;
  }, {});
};

export const getEmptyFilters = () =>
  FORM_FILLS_FILTERS_URL_MAP.reduce((acc, { key, isArray }) => {
    acc[key] = isArray ? [] : null;
    return acc;
    }, {});